import { useTranslation } from 'react-i18next';
import type { CategoryStatus } from '../completeness.api';

/** Fixed display order matching the approved mockup */
const CATEGORY_ORDER = ['LABEL', 'PHOTO', 'MARKETING', 'PACKINFO', 'HAZARDOUS'] as const;

/** MDI icon class per category */
const CATEGORY_ICONS: Record<string, string> = {
  LABEL: 'mdi-nutrition',
  PHOTO: 'mdi-camera-outline',
  MARKETING: 'mdi-bullhorn-outline',
  PACKINFO: 'mdi-package-variant',
  HAZARDOUS: 'mdi-alert-outline',
};

type IndicatorStatus = 'complete' | 'attention' | 'incomplete' | 'na';

function getStatus(cat: CategoryStatus): IndicatorStatus {
  if (!cat.applicable) return 'na';
  if (cat.score === 100) return 'complete';
  if (cat.score > 0) return 'attention';
  return 'incomplete';
}

const STATUS_STYLES: Record<IndicatorStatus, { bg: string; color: string; opacity?: number }> = {
  complete: { bg: 'rgba(183, 217, 69, 0.15)', color: 'var(--success-dark)' },
  attention: { bg: 'rgba(232, 163, 23, 0.12)', color: 'var(--warning)' },
  incomplete: { bg: 'rgba(214, 69, 69, 0.10)', color: 'var(--destructive)' },
  na: { bg: 'rgba(148, 163, 184, 0.08)', color: '#94A3B8', opacity: 0.7 },
};

interface CategoryIndicatorsProps {
  categories?: CategoryStatus[];
}

export function CategoryIndicators({ categories }: CategoryIndicatorsProps) {
  const { t } = useTranslation();

  if (!categories || categories.length === 0) return null;

  const catMap = new Map(categories.map((c) => [c.categoryId, c]));

  return (
    <div data-testid="category-indicators" className="flex items-center gap-[3px]">
      {CATEGORY_ORDER.map((catId) => {
        const cat = catMap.get(catId);
        // Category missing from DQS response → treat as n.v.t.
        const status = cat ? getStatus(cat) : 'na';
        const style = STATUS_STYLES[status];
        const label = t(`categories.${catId}`);
        const tooltip = status === 'na'
          ? `${label}: ${t('categories.notApplicable')}`
          : `${label}: ${cat!.score}%`;

        return (
          <span
            key={catId}
            data-testid={`cat-${catId.toLowerCase()}`}
            title={tooltip}
            className="inline-flex items-center justify-center rounded-md"
            style={{
              width: 24,
              height: 24,
              backgroundColor: style.bg,
              color: style.color,
              opacity: style.opacity,
            }}
          >
            <span
              className={`mdi ${CATEGORY_ICONS[catId]}`}
              style={{ fontSize: 16, lineHeight: 1 }}
              aria-hidden="true"
            />
          </span>
        );
      })}
    </div>
  );
}
